import { useEffect, useMemo, useRef } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  bookingsByDateOnly,
  buildEventColorTheme,
  EVENT_COLOR_CLASSES,
  getWeekdayHeadersTwo,
  isSameDay,
  isToday,
  toDateKey,
  weekdayGridColsClass,
} from "./calendar-utils";
import { canMutateAppointmentEvent } from "./calendar-drag-geometry";
import { BlockChip, blockTheme, blocksForDate } from "./calendar-blocks";

/**
 * Desktop month-grid day cell as a drop target: dropping an appointment here
 * changes its date, keeping the wall-clock time and duration (handled by the
 * calendar's `month-day` drag-end branch).
 */
function MonthDayCellDroppable({ dayKey, className, style, children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `month:${dayKey}`,
    data: { type: "month-day", dayKey },
  });
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(className, isOver && "ring-2 ring-inset ring-primary/50")}
    >
      {children}
    </div>
  );
}

/**
 * Month event pill, draggable when the appointment is editable. The click that
 * lands right after a drop is already swallowed by dnd-kit's document-level
 * capture-phase click suppression (kept alive ~50ms past drag end), so no
 * extra guard is needed — one would swallow the NEXT genuine click instead.
 */
function MonthEventPill({ event, onActivate, className, style, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `month:${event.id}`,
    data: { type: "appointment", event },
    disabled: !canMutateAppointmentEvent(event),
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onActivate}
      style={style}
      className={cn(className, "touch-none", isDragging && "opacity-40")}
      {...attributes}
      {...listeners}
    >
      {children}
    </button>
  );
}

// Desktop day-cell height at 100% zoom. Month has no time axis, so "zoom" scales this cell
// floor: zooming in grows the cells (more events visible, the grid scrolls vertically),
// zooming out shrinks them back toward content height.
const BASE_MONTH_CELL_REM = 6.5;

const EVENT_DOT_COLOR_CLASSES = {
  blue: "bg-blue-500",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  orange: "bg-orange-500",
  cyan: "bg-cyan-500",
  rose: "bg-rose-500",
  amber: "bg-amber-500",
};

function eventItemColorClass(event) {
  const isSlot = event.kind === "slot";
  const customTheme = buildEventColorTheme(event.colorHex);
  if (customTheme) {
    // Slot pill: dashed border, ghost fill — visually "available placeholder".
    // Booking pill: solid border, filled tint — visually "committed".
    return isSlot
      ? "opacity-(--calendar-slot-opacity) border-dashed bg-(--event-bg-ghost-light) text-(--event-text-light) border-(--event-border-light) hover:bg-(--event-bg-ghost-hover-light) dark:bg-(--event-bg-ghost-dark) dark:text-(--event-text-dark) dark:border-(--event-border-dark) dark:hover:bg-(--event-bg-ghost-hover-dark)"
      : "bg-(--event-bg-light) text-(--event-text-light) border-(--event-border-light) hover:bg-(--event-bg-hover-light) dark:bg-(--event-bg-dark) dark:text-(--event-text-dark) dark:border-(--event-border-dark) dark:hover:bg-(--event-bg-hover-dark)";
  }
  if (isSlot) {
    // Default (no calendar tint) slot pill: dashed sky outline + transparent ghost,
    // faded by the user's "Slot opacity" setting (CSS var on the calendar wrapper).
    return "opacity-(--calendar-slot-opacity) border-dashed border-sky-300 bg-sky-50/40 text-sky-900 hover:bg-sky-50/70 dark:border-sky-500/40 dark:bg-sky-950/30 dark:text-sky-200/90 dark:hover:bg-sky-950/45";
  }
  return EVENT_COLOR_CLASSES[event.color] || EVENT_COLOR_CLASSES.blue;
}

function MonthView({
  monthCells,
  eventsByDate,
  selectedDate,
  onSelectDate,
  onSelectEvent,
  timeOffBlocks,
  doctorOptions,
  hideSunday = false,
  monthZoomPercent = 100,
  registerViewport,
  zoomAnchorRef,
  resetSignal = 0,
}) {
  const weekdayHeaders = getWeekdayHeadersTwo({ hideSunday });
  const colsClass = weekdayGridColsClass(hideSunday);
  const lgColsClass = hideSunday ? "lg:grid-cols-6" : "lg:grid-cols-7";
  // Month is a month-at-a-glance of what is BOOKED. A clinic generates dozens of
  // open slots a day and books a handful, so slots would fill every cell's two
  // visible rows and push the actual appointments into "+ N more". They are
  // dropped here regardless of the "Slots" display setting, which still governs
  // the day and week grids. The month fetch already asks for bookings only; this
  // also covers events left over from another view or arriving over the socket.
  const bookingsByDate = useMemo(() => bookingsByDateOnly(eventsByDate), [eventsByDate]);
  const selectedEvents = bookingsByDate[toDateKey(selectedDate)] || [];
  const scrollAreaRef = useRef(null);
  const prevMonthZoomRef = useRef(monthZoomPercent);
  const cellMinHeightRem = (BASE_MONTH_CELL_REM * monthZoomPercent) / 100;

  // Register this view's scroll viewport for the shared zoom/pan hook.
  useEffect(() => {
    if (typeof registerViewport !== "function") {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    registerViewport(viewport || null);
    return () => registerViewport(null);
  }, [registerViewport]);

  // Zoom compensation: keep the point under the cursor (wheel zoom) — or the viewport centre
  // (Display-popover zoom) — anchored as the cells grow/shrink. Ratio-based since month has
  // no time axis to convert to minutes.
  useEffect(() => {
    if (prevMonthZoomRef.current === monthZoomPercent) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      prevMonthZoomRef.current = monthZoomPercent;
      return undefined;
    }
    const anchor = zoomAnchorRef?.current?.view === "month" ? zoomAnchorRef.current : null;
    const prevScrollHeight = Math.max(anchor?.prevScrollHeight ?? viewport.scrollHeight, 1);
    const focusContentY = anchor ? anchor.contentY : viewport.scrollTop + viewport.clientHeight / 2;
    const ratio = Math.min(Math.max(focusContentY / prevScrollHeight, 0), 1);
    const focusOffsetY = anchor ? anchor.pointerOffsetY : viewport.clientHeight / 2;
    const id = window.requestAnimationFrame(() => {
      const nextScrollHeight = Math.max(viewport.scrollHeight, 1);
      const nextScrollTop = ratio * nextScrollHeight - focusOffsetY;
      const maxScrollTop = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
      viewport.scrollTop = Math.min(Math.max(nextScrollTop, 0), maxScrollTop);
      if (anchor && zoomAnchorRef) {
        zoomAnchorRef.current = null;
      }
    });
    prevMonthZoomRef.current = monthZoomPercent;
    return () => window.cancelAnimationFrame(id);
  }, [monthZoomPercent]);

  // Quick-navigation reset: scroll back to the top of the month grid (double-rAF so it lands
  // after the 100%-zoom relayout).
  useEffect(() => {
    if (!resetSignal) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        viewport.scrollTop = 0;
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [resetSignal]);
  const doctorNameById = useMemo(() => {
    const map = new Map();
    if (Array.isArray(doctorOptions)) {
      for (const opt of doctorOptions) {
        const id = String(opt?.id || "").trim();
        const name = String(opt?.name || "").trim();
        if (id && name) map.set(id, name);
      }
    }
    return map;
  }, [doctorOptions]);
  const blocksByDayKey = useMemo(() => {
    const map = new Map();
    if (Array.isArray(monthCells)) {
      for (const cell of monthCells) {
        map.set(toDateKey(cell.date), blocksForDate(timeOffBlocks, cell.date));
      }
    }
    return map;
  }, [monthCells, timeOffBlocks]);

  return (
    <ScrollArea ref={scrollAreaRef} className="h-full min-h-0">
      <div className="lg:flex lg:min-h-0 lg:flex-col">
        <div className="shadow-sm ring-1 ring-border/60 lg:flex-none">
          <div className={cn("grid gap-px border-b border-border bg-muted/50 text-center text-xs/6 font-semibold text-muted-foreground", colsClass)}>
            {weekdayHeaders.map((label) => (
              <div key={label} className="flex justify-center bg-background py-2">
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="flex bg-muted/50 text-xs/6 text-muted-foreground">
          <div
            className={cn("hidden w-full lg:grid lg:grid-rows-6 lg:gap-px", lgColsClass)}
            style={{ "--month-cell-h": `${cellMinHeightRem}rem` }}
          >
            {monthCells.map((cell) => {
              const dayKey = toDateKey(cell.date);
              const dayEvents = bookingsByDate[dayKey] || [];
              const isSelected = isSameDay(cell.date, selectedDate);
              const dayBlocks = blocksByDayKey.get(dayKey) || [];
              // Holidays paint the cell tint + corner icon; per-doctor blocks just
              // render as a chip in the events list.
              const holidayBlock = dayBlocks.find((b) => b.type === "holiday") || null;
              const cellTint = holidayBlock ? blockTheme(holidayBlock).cellTintClass : "";
              const visibleBlocks = dayBlocks.slice(0, 1);
              const remainingBlocks = dayBlocks.length - visibleBlocks.length;
              // Taller (zoomed-in) cells fit more events; 2 at 100% (no change from before).
              const eventBudgetBase = Math.max(2, Math.round((2 * monthZoomPercent) / 100));
              const eventBudget = Math.max(0, eventBudgetBase - visibleBlocks.length);
              const visibleEvents = dayEvents.slice(0, eventBudget);
              const hiddenCount =
                Math.max(0, dayEvents.length - visibleEvents.length) +
                Math.max(0, remainingBlocks);
              return (
                <MonthDayCellDroppable
                  key={dayKey}
                  dayKey={dayKey}
                  style={{ minHeight: "var(--month-cell-h)" }}
                  className={cn(
                    "group relative px-3 py-2",
                    cell.isCurrentMonth ? "bg-background" : "bg-muted/30 text-muted-foreground",
                    cellTint,
                    isSelected && "ring-1 ring-inset ring-primary/30",
                  )}
                >
                  <div className="flex items-start justify-between">
                    <time
                      dateTime={dayKey}
                      className={cn(
                        "relative",
                        isToday(cell.date) &&
                          "flex size-6 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground",
                      )}
                    >
                      {cell.date.day}
                    </time>
                    {holidayBlock ? (
                      (() => {
                        const theme = blockTheme(holidayBlock);
                        const Icon = theme.Icon;
                        return (
                          <Icon
                            className={cn("size-3.5 shrink-0", theme.iconClass)}
                            aria-label={theme.label}
                          />
                        );
                      })()
                    ) : null}
                  </div>
                  {dayBlocks.length + dayEvents.length > 0 ? (
                    <ol className="mt-2 space-y-1">
                      {visibleBlocks.map((block) => (
                        <li key={block.id}>
                          <BlockChip
                            block={block}
                            doctorName={
                              block.scope !== "global" && block.ownerId
                                ? doctorNameById.get(block.ownerId) || null
                                : null
                            }
                            className="w-full justify-start"
                          />
                        </li>
                      ))}
                      {visibleEvents.map((event) => {
                        const isSlot = event.kind === "slot";
                        return (
                          <li key={event.id}>
                            <MonthEventPill
                              event={event}
                              onActivate={() => (onSelectEvent ? onSelectEvent(cell.date, event) : onSelectDate(cell.date))}
                              style={buildEventColorTheme(event.colorHex) || undefined}
                              className={cn(
                                "flex w-full items-center rounded-md border border-transparent px-2 py-1.5 text-left text-xs transition hover:ring-1 hover:ring-foreground/10",
                                eventItemColorClass(event),
                              )}
                            >
                              {/* Slot: dashed ring (hollow). Booking: filled dot. Same shape language as the day/week cards. */}
                              <span
                                className={cn(
                                  "mr-2 size-1.5 shrink-0 rounded-full",
                                  isSlot
                                    ? cn(
                                        "border border-dashed bg-transparent",
                                        event.colorHex
                                          ? "border-(--event-dot-light) dark:border-(--event-dot-dark)"
                                          : "border-sky-500/70 dark:border-sky-400/60",
                                      )
                                    : event.colorHex
                                      ? "bg-(--event-dot-light) dark:bg-(--event-dot-dark)"
                                      : EVENT_DOT_COLOR_CLASSES[event.color] || "bg-muted-foreground/70",
                                )}
                              />
                              <span
                                className={cn(
                                  "min-w-0 flex-1 truncate",
                                  isSlot ? "font-medium italic" : "font-medium",
                                )}
                              >
                                {event.patient || event.title || (isSlot ? "Open" : "")}
                              </span>
                              <time className="ml-2 hidden shrink-0 opacity-80 xl:block">
                                {event.startAt.toFormat("HH:mm")}
                              </time>
                            </MonthEventPill>
                          </li>
                        );
                      })}
                      {hiddenCount > 0 ? (
                        <li className="px-2 py-1 text-muted-foreground">+ {hiddenCount} more</li>
                      ) : null}
                    </ol>
                  ) : null}
                </MonthDayCellDroppable>
              );
            })}
          </div>

          <div className={cn("isolate grid w-full grid-rows-6 gap-px lg:hidden", colsClass)}>
            {monthCells.map((cell) => {
              const dayKey = toDateKey(cell.date);
              const dayEvents = bookingsByDate[dayKey] || [];
              const selected = isSameDay(cell.date, selectedDate);
              return (
                <button
                  key={dayKey}
                  type="button"
                  onClick={() => onSelectDate(cell.date)}
                  className={cn(
                    "group relative flex h-14 flex-col px-3 py-2 hover:bg-muted focus:z-10",
                    cell.isCurrentMonth ? "bg-background text-foreground" : "bg-muted/30 text-muted-foreground",
                    selected && "font-semibold",
                  )}
                >
                  <time
                    dateTime={toDateKey(cell.date)}
                    className={cn(
                      "ml-auto",
                      selected &&
                        !isToday(cell.date) &&
                        "flex size-6 items-center justify-center rounded-full bg-foreground text-background",
                      isToday(cell.date) &&
                        "flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground",
                    )}
                  >
                    {cell.date.day}
                  </time>
                  {dayEvents.length > 0 ? (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                      {dayEvents.slice(0, 3).map((event) => {
                        const isSlot = event.kind === "slot";
                        return (
                          <span
                            key={event.id}
                            style={buildEventColorTheme(event.colorHex) || undefined}
                            className={cn(
                              "mx-0.5 mb-1 size-1.5 rounded-full",
                              // Slot: dashed ring; Booking: filled dot. Mirrors day/week card shape language at the smallest scale.
                              isSlot
                                ? cn(
                                    "border border-dashed bg-transparent",
                                    event.colorHex
                                      ? "border-(--event-dot-light) dark:border-(--event-dot-dark)"
                                      : "border-sky-500/70 dark:border-sky-400/60",
                                  )
                                : event.colorHex
                                  ? "bg-(--event-dot-light) dark:bg-(--event-dot-dark)"
                                  : EVENT_DOT_COLOR_CLASSES[event.color] || "bg-muted-foreground/70",
                            )}
                          />
                        );
                      })}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          </div>
        </div>

        <div className="relative px-4 py-10 sm:px-6 lg:hidden">
          <ol className="space-y-2 overflow-hidden rounded-lg text-sm">
            {selectedEvents.map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => (onSelectEvent ? onSelectEvent(selectedDate, event) : null)}
                  style={buildEventColorTheme(event.colorHex) || undefined}
                  className={cn(
                    "flex w-full flex-col rounded-lg border border-transparent p-4 text-left shadow-sm transition hover:ring-1 hover:ring-foreground/10",
                    eventItemColorClass(event),
                  )}
                >
                  <p className="truncate font-semibold">{event.patient || "Unknown patient"}</p>
                  <p className="mt-1 truncate opacity-80">{event.title}</p>
                  <time className="mt-2 flex items-center truncate opacity-80">
                    {event.startAt.toFormat("HH:mm")}
                  </time>
                </button>
              </li>
            ))}
            {selectedEvents.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">No events for the selected date.</li>
            ) : null}
          </ol>
        </div>
      </div>
    </ScrollArea>
  );
}

export default MonthView;
